/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Button,
  Card,
  Container,
  Col,
  Form,
  InputGroup,
  Row,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import axios from 'axios';

import AuthHeader from 'components/Headers/AuthHeader.js';

import { auth } from '_actions/userActions';
import { login } from 'apis/admin/userApi';
import { useAuth } from 'context/auth';

function Login(props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    mode: 'onSubmit',
    shouldFocusError: true,
  });

  const dispatch = useDispatch();

  const [IsSaved, setIsSaved] = useState(false);
  const { setRefresh } = useAuth();

  useEffect(() => {
    if (window.localStorage.getItem(`${process.env.REACT_APP_COOKIE_NAME}_admin_identification`)) {
      setValue(
        'userid',
        window.localStorage.getItem(`${process.env.REACT_APP_COOKIE_NAME}_admin_identification`)
      );
      setIsSaved(true);
    }
  }, [IsSaved, setValue]);

  const handleSaveId = () => {
    if (window.localStorage.getItem(`${process.env.REACT_APP_COOKIE_NAME}_admin_identification`)) {
      setIsSaved(false);
      window.localStorage.removeItem(`${process.env.REACT_APP_COOKIE_NAME}_admin_identification`);
    } else {
      if (!getValues('userid')) {
        setIsSaved(false);

        return Swal.fire({
          text: '아이디를 먼저 입력하세요.',
        });
      }
      setIsSaved(true);
      window.localStorage.setItem(
        `${process.env.REACT_APP_COOKIE_NAME}_admin_identification`,
        getValues('userid')
      );
    }
  };

  const onSubmit = async (data) => {
    const dataToSubmit = { ...data };

    await login(dataToSubmit)
      .then(async (response) => {
        const { accessToken, refreshToken } = response.data;

        axios.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${accessToken}`;
        await setRefresh(accessToken, refreshToken);
        dispatch(auth()).then((response) => {
          // not sign in
          if (!response.payload.isAuth) {
            props.history.push('/');
          } else {
            // sign in
            if (response.payload.isAdmin) {
              // for admin user
              props.history.replace('/admin/home');
            } else {
              // for not sign in user
              props.history.push('/');
            }
          }
        });
      })
      .catch((error) => {
        console.error('error occured in Login.js - onSubmit(data) ', error);

        Swal.fire({
          title: '아이디 또는 비밀번호가 맞지 않습니다.',
          icon: 'error',
          confirmButtonText: '확인',
        });
      });
  };

  return (
    <>
      <AuthHeader />
      <Container>
        <Row className="justify-content-center">
          <Col lg="5" md="7">
            <Card className="bg-white border-0 mb-0">
              <Card.Body className="px-lg-5 py-lg-5">
                <div className="text-center mb-5 font-weight-600">관리자 페이지 로그인</div>
                <Form role="form" onSubmit={handleSubmit(onSubmit)}>
                  <Form.Group as={Row} className="mb-3">
                    <InputGroup className="input-group-alternative">
                      <InputGroup.Text>
                        <i className="fas fa-user-alt" />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="아이디"
                        id="userid"
                        defaultValue={getValues('userid') || ''}
                        {...register('userid', {
                          required: '아이디는 필수 입력 항목입니다',
                        })}
                        onChange={(event) => {
                          setValue('userid', event.target.value);
                          if (IsSaved) {
                            window.localStorage.setItem(
                              `${process.env.REACT_APP_COOKIE_NAME}_admin_identification`,
                              getValues('userid')
                            );
                          }
                        }}
                        isInvalid={errors && errors.userid}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.userid && errors.userid.message}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  <Form.Group as={Row}>
                    <InputGroup className="input-group-alternative">
                      <InputGroup.Text>
                        <i className="fas fa-unlock-alt" />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="비밀번호"
                        type="password"
                        {...register('password', {
                          required: '비밀번호는 필수 입력 항목입니다',
                        })}
                        isInvalid={errors && errors.password}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password && errors.password.message}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  <Form.Group as={Row}>
                    <Form.Check
                      type="checkbox"
                      label="아이디 기억하기"
                      className="text-sm"
                      id="remember-id"
                      checked={IsSaved}
                      onChange={handleSaveId}
                    />
                  </Form.Group>
                  <div className="text-center mt-3">
                    <Button
                      className="my-2 pl-5 pr-5"
                      variant="dark"
                      type="submit"
                    >
                      로그인
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Login;
